import { NavLink, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef, type CSSProperties } from 'react'
import clsx from 'clsx'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useAuth } from '@/hooks/useAuth'
import { canViewSidebarItem } from '@/config/routePermissions'
import { Permission, type PermissionType } from '@/types/auth'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useSidebarTheme } from '@/contexts/sidebar-theme-context'
import { useOrbAnimationCycle } from '@/hooks/useOrbAnimationCycle'
import { ResilienceIntelligenceOrb } from '@/assets/ResilienceIntelligenceOrb'
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Home,
  Building2,
  FlaskConical,
  ClipboardCheck,
  AlertTriangle,
  Flame,
  ShieldCheck,
  FileText,
  BarChart3,
  PieChart,
  Link,
  Box,
  Database,
  Server,
  Users,
  Wrench,
  MapPin,
  Truck,
  Table2,
  Sparkles,
  ClipboardList,
  ArrowLeftRight,
  Rocket,
  Heart,
  Globe,
  Zap,
  SlidersHorizontal,
  RefreshCw,
  CloudUpload,
  Upload,
  Tag,
  Maximize2,
  HardDrive,
  Activity,
  Calendar,
  Layers,
  Target,
  BookOpen,
  FileSearch,
  Gauge,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface SidebarProps {
  isMobileOpen?: boolean
  onMobileClose?: () => void
}

type FeatureFlagKey = 'consumerDutyIntegration' | 'aiAnalytics' | 'advancedReporting'

/**
 * Routes that should keep the "Operational Components" sidebar entry
 * highlighted. These are the detail / sub-routes for each component type
 * surfaced on the Operational Components tabbed page. Keeping the highlight
 * across these routes avoids the sidebar losing context when the user
 * navigates into a single record from a list view.
 */
const OPERATIONAL_COMPONENT_DETAIL_PREFIXES = [
  '/performance-hub/operational-components',
  '/microservices',
  '/systems-registry',
  '/infrastructure-registry',
  '/locations-registry',
  '/teams',
  '/teams-registry',
  '/suppliers-registry',
  '/critical-data',
  '/relationships',
]

// eslint-disable-next-line react-refresh/only-export-components -- exported for sidebar regression tests
export function isOperationalComponentsRoute(pathname: string): boolean {
  return OPERATIONAL_COMPONENT_DETAIL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  )
}

/**
 * Routes that should keep the "IBS Dashboard" sidebar entry highlighted.
 * Includes the dashboard itself plus all single-IBS detail routes (overview,
 * flows, microservices) which sit under /ibs/:hubIbsId rather than nesting
 * beneath /performance-hub/ibs.
 *
 * The IBS-create / IBS-continue / flow-builder wizards are deliberately
 * excluded — they are launched from the Configure IBSs page and remain
 * anchored to that sidebar entry (see isConfigureIbsRoute).
 */
const IBS_DASHBOARD_DETAIL_PREFIXES = ['/performance-hub/ibs', '/ibs']

// Routes that should keep the "Configure IBSs" sidebar entry highlighted
// when the user is inside a creation / continue wizard launched from the
// Configure IBSs page. Covers the top-level IBS wizards (/ibs/:id/edit and
// /ibs/:id/continue) plus the nested E2E Flow Builder wizards which are
// reached via the Configure IBSs > E2E Flows tab.
const CONFIGURE_IBS_WIZARD_PATTERN =
  /^\/ibs\/[^/]+\/(edit|continue|flows\/create|flows\/[^/]+\/edit)$/

// eslint-disable-next-line react-refresh/only-export-components -- exported for sidebar regression tests
export function isIbsDashboardRoute(pathname: string): boolean {
  if (CONFIGURE_IBS_WIZARD_PATTERN.test(pathname)) return false
  return IBS_DASHBOARD_DETAIL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for sidebar regression tests
export function isConfigureIbsRoute(pathname: string): boolean {
  if (pathname === '/configure-hub/ibs' || pathname.startsWith('/configure-hub/ibs/')) {
    return true
  }
  return CONFIGURE_IBS_WIZARD_PATTERN.test(pathname)
}

interface NavItem {
  name: string
  href?: string
  icon: LucideIcon
  featureFlag?: FeatureFlagKey
  children?: NavItem[]
  requireClientAdmin?: boolean
  hideIfClientAdmin?: boolean
  // OR-312: when set, the user must hold ALL listed permissions for the
  // item to be visible (AND semantics). Used for the Configure Hub
  // Overview link, which is shown only to users with both IBS edit and
  // Components edit. Other items continue to use the OR semantics of
  // canViewSidebarItem via SIDEBAR_PERMISSIONS.
  requireAllPermissions?: PermissionType[]
}

interface NavSection {
  title: string
  items: NavItem[]
  // When true the section header label is not rendered, so the section's
  // items appear as standalone top-level entries. Used for the Command
  // Centre, which sits above the Performance Hub as a single item without
  // a redundant group label (mirrors the Consumer Duty sidebar).
  hideTitle?: boolean
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for OR-74 sidebar regression tests
export const navigationSections: NavSection[] = [
  {
    // Standalone top-level entry above the Performance Hub. The header is
    // suppressed (hideTitle) so it renders as a single "Command Centre"
    // item rather than a "Command Centre" group containing a lone
    // "Command Centre" link.
    title: 'Command Centre',
    hideTitle: true,
    items: [
      {
        name: 'Command Centre',
        href: '/command-centre',
        icon: Gauge,
      },
    ],
  },
  {
    title: 'Performance Hub',
    items: [
      {
        name: 'Dashboard',
        href: '/',
        icon: Home,
      },
      {
        name: 'IBS Dashboard',
        href: '/performance-hub/ibs',
        icon: Building2,
      },
      {
        name: 'Operational Components',
        href: '/performance-hub/operational-components',
        icon: Layers,
      },
      {
        name: 'Data Submission',
        href: '/performance-hub/bulk-performance-upload',
        icon: Upload,
      },
    ],
  },
  {
    title: 'Testing Hub',
    items: [
      {
        name: 'Scenario Wizard',
        href: '/scenarios/wizard',
        icon: Wrench,
      },
      {
        name: 'Scenario Library',
        href: '/scenarios/library',
        icon: FileText,
      },
      {
        name: 'CMORG Library',
        href: '/scenarios/cmorg-library',
        icon: ShieldCheck,
      },
      {
        name: 'Client Vault',
        href: '/knowledge-hub',
        icon: BookOpen,
      },
    ],
  },
  {
    title: 'Risk Intelligence',
    items: [
      {
        name: 'Risks',
        href: '/risks',
        icon: AlertTriangle,
      },
      {
        name: 'Incidents',
        href: '/incidents',
        icon: Flame,
      },
    ],
  },
  {
    title: 'Configure Hub',
    items: [
      {
        name: 'Overview',
        href: '/admin',
        icon: Home,
        // OR-312: Overview is visible only to users who hold both
        // ConfigureHub.IBS.Edit AND ConfigureHub.Components.Edit (the
        // derived 'sees the whole Configure Hub' shape).
        requireAllPermissions: [Permission.IBS_UPDATE, Permission.COMPONENTS_EDIT],
      },
      {
        name: 'Configure IBSs',
        href: '/configure-hub/ibs',
        icon: Building2,
      },
      {
        name: 'Configure Components',
        href: '/configure-hub/components',
        icon: SlidersHorizontal,
      },
    ],
  },
  {
    title: 'Admin',
    items: [
      {
        name: 'User Management',
        href: '/client-admin/users',
        icon: Users,
      },
      {
        name: 'Role Management',
        href: '/client-admin/roles',
        icon: ShieldCheck,
      },
    ],
  },
]

export default function Sidebar({ isMobileOpen = false, onMobileClose }: SidebarProps) {
  const location = useLocation()
  const { flags } = useFeatureFlags()
  const { isClientAdmin, authUser } = useAuth()
  const userPermissions = authUser?.permissions || []
  const { theme } = useSidebarTheme()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [flyoutItem, setFlyoutItem] = useState<string | null>(null)
  const [flyoutTop, setFlyoutTop] = useState(0)
  const { playing: orbPlaying, triggerCycle: triggerOrbCycle } = useOrbAnimationCycle(3000)
  const flyoutTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    const expanded: string[] = []
    if (location.pathname.startsWith('/ibs')) {
      expanded.push('IBS')
    }
    if (location.pathname.startsWith('/admin')) {
      expanded.push('Admin')
    }
    return expanded
  })

  const [manuallyCollapsed, setManuallyCollapsed] = useState<string[]>([])

  useEffect(() => {
    if (location.pathname.startsWith('/ibs')
        && !expandedItems.includes('IBS') && !manuallyCollapsed.includes('IBS')) {
      setExpandedItems(prev => [...prev, 'IBS'])
    }
    if (location.pathname.startsWith('/admin') && !expandedItems.includes('Admin') && !manuallyCollapsed.includes('Admin')) {
      setExpandedItems(prev => [...prev, 'Admin'])
    }
  }, [location.pathname, expandedItems, manuallyCollapsed])

  // Close mobile sidebar on location change
  useEffect(() => {
    if (isMobileOpen && onMobileClose) {
      onMobileClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const isFeatureEnabled = (item: NavItem): boolean => {
    if (item.requireClientAdmin && !isClientAdmin) return false
    if (item.hideIfClientAdmin && isClientAdmin) return false
    // RBAC v1.2: Client admins have access to all features.
    // Other users are checked against their assigned role permissions.
    if (!isClientAdmin) {
      // OR-312: AND-style permission gate (must hold every listed perm).
      if (item.requireAllPermissions && item.requireAllPermissions.length > 0) {
        const hasAll = item.requireAllPermissions.every(p => userPermissions.includes(p))
        if (!hasAll) return false
      }
      // Existing OR-style gate via SIDEBAR_PERMISSIONS (at least one match).
      if (item.href && !canViewSidebarItem(item.href, userPermissions)) return false
    }
    // For parent items with children, hide if all children are hidden
    if (item.children && item.children.length > 0) {
      const hasVisibleChild = item.children.some(child => isFeatureEnabled(child))
      if (!hasVisibleChild) return false
    }
    if (!item.featureFlag) return true
    return flags[item.featureFlag] ?? false
  }

  const toggleItem = (itemName: string) => {
    const isExpanded = expandedItems.includes(itemName)
    if (isExpanded) {
      setExpandedItems(prev => prev.filter(s => s !== itemName))
      setManuallyCollapsed(prev => [...prev, itemName])
    } else {
      setExpandedItems(prev => [...prev, itemName])
      setManuallyCollapsed(prev => prev.filter(s => s !== itemName))
    }
  }

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed)
  }

  const isActive = (href: string) => {
    if (href === '/' || href === '/admin') return location.pathname === href
    if (href === '/performance-hub/operational-components') {
      return isOperationalComponentsRoute(location.pathname)
    }
    if (href === '/performance-hub/ibs') {
      return isIbsDashboardRoute(location.pathname)
    }
    if (href === '/configure-hub/ibs') {
      return isConfigureIbsRoute(location.pathname)
    }
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  // Theme-aware styling helpers - matches CD sidebar pattern
  const getBaseClasses = () => {
    if (isCollapsed) {
      return 'py-2 justify-center'
    }
    if (theme === 'original') {
      return 'px-4 py-2'
    }
    return 'px-4 py-2 ml-3 rounded-l-lg'
  }

  const getChildBaseClasses = () => {
    if (theme === 'original') {
      return 'pl-12 pr-4 py-2'
    }
    return 'pl-12 pr-4 py-2 ml-3 rounded-l-lg'
  }

  const getActiveClass = () => {
    if (theme === 'original') {
      return 'bg-sidebar-primary text-sidebar-primary-foreground'
    }
    return 'text-white'
  }

  const getInactiveClass = () => {
    return 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
  }

  const getActiveStyle = (): CSSProperties => {
    if (theme === 'original') {
      return {}
    }
    return {
      background: 'linear-gradient(to top right, var(--sidebar-gradient-from), var(--sidebar-gradient-to))'
    }
  }

  const renderNavItem = (item: NavItem) => {
    if (item.children) {
      const isExpanded = expandedItems.includes(item.name)
      const isChildActive = item.children.some(
        child => child.href && isActive(child.href)
      )

      return (
        <li key={item.name}>
          <button
            onClick={() => !isCollapsed && toggleItem(item.name)}
            onMouseEnter={(e) => {
              if (isCollapsed) {
                if (flyoutTimeout.current) { clearTimeout(flyoutTimeout.current); flyoutTimeout.current = null }
                const top = e.currentTarget.getBoundingClientRect().top
                setFlyoutTop(top)
                setFlyoutItem(item.name)
              }
            }}
            onMouseLeave={() => {
              if (isCollapsed) {
                flyoutTimeout.current = setTimeout(() => setFlyoutItem(null), 300)
              }
            }}
            className={clsx(
              'group flex w-full items-start transition-all duration-300 ease-in-out',
              getBaseClasses(),
              isCollapsed && isChildActive
                ? getActiveClass()
                : getInactiveClass()
            )}
            style={isCollapsed && isChildActive ? getActiveStyle() : {}}
          >
            <item.icon className={clsx('h-5 w-5 min-w-5 mt-0.5', !isCollapsed && 'mr-3')} aria-hidden="true" />
            {isCollapsed && <span className="sr-only">{item.name}</span>}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.name}</span>
                <ChevronDown
                  className={clsx(
                    'h-4 w-4 mr-3 mt-0.5 transition-transform duration-200',
                    isExpanded ? 'rotate-0' : '-rotate-90'
                  )}
                />
              </>
            )}
          </button>
          {/* Flyout menu when collapsed */}
          {isCollapsed && flyoutItem === item.name && (
            <div
              className="fixed z-50"
              style={{ left: '3rem' }}
              ref={(el) => {
                if (el) {
                  const height = el.getBoundingClientRect().height
                  const maxTop = window.innerHeight - height - 8
                  el.style.top = `${Math.min(flyoutTop - 8, maxTop)}px`
                }
              }}
              onMouseEnter={() => { if (flyoutTimeout.current) { clearTimeout(flyoutTimeout.current); flyoutTimeout.current = null } }}
              onMouseLeave={() => { flyoutTimeout.current = setTimeout(() => setFlyoutItem(null), 300) }}
            >
              {/* Invisible bridge covering gap between sidebar and flyout */}
              <div className="absolute -left-2 top-0 w-2 h-full" />
            <div
              className="min-w-[200px] bg-popover border border-border rounded-md shadow-lg py-1"
            >
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                {item.name}
              </div>
              {item.children.filter(isFeatureEnabled).map((child) => {
                const childActive = child.href && isActive(child.href)
                return (
                  <NavLink
                    key={child.name}
                    to={child.href!}
                    className={clsx(
                      'flex items-center px-3 py-2 text-sm transition-colors',
                      childActive
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-popover-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                    onClick={() => setFlyoutItem(null)}
                  >
                    <child.icon className="h-4 w-4 mr-2" />
                    {child.name}
                  </NavLink>
                )
              })}
            </div>
            </div>
          )}
          {isExpanded && !isCollapsed && (
            <ul className="mt-1">
              {item.children.filter(isFeatureEnabled).map((child) => {
                if (child.children) {
                  return renderNavItem(child)
                }

                const childActive = child.href && isActive(child.href)

                return (
                  <li key={child.name}>
                    <NavLink
                      to={child.href!}
                      className={clsx(
                        'group flex items-start transition-all duration-300 ease-in-out',
                        getChildBaseClasses(),
                        childActive
                          ? getActiveClass()
                          : getInactiveClass()
                      )}
                      style={childActive ? getActiveStyle() : {}}
                    >
                      <child.icon className={clsx('h-5 w-5 min-w-5 mt-0.5', !isCollapsed && 'mr-3')} />
                      {child.name}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          )}
        </li>
      )
    }

    const active = item.href && isActive(item.href)

    return (
      <li key={item.name} onMouseEnter={() => { if (flyoutItem) { if (flyoutTimeout.current) clearTimeout(flyoutTimeout.current); setFlyoutItem(null) } }}>
        <NavLink
          to={item.href!}
          className={clsx(
            'group flex items-start transition-all duration-300 ease-in-out',
            getBaseClasses(),
            active
              ? getActiveClass()
              : getInactiveClass()
          )}
          style={active ? getActiveStyle() : {}}
        >
          <item.icon className={clsx('h-5 w-5 min-w-5 mt-0.5', !isCollapsed && 'mr-3')} aria-hidden="true" />
          {isCollapsed ? <span className="sr-only">{item.name}</span> : <span>{item.name}</span>}
        </NavLink>
      </li>
    )
  }

  return (
    <aside
      className={clsx(
        'sidebar bg-sidebar text-sidebar-foreground flex-shrink-0 flex flex-col h-full fixed md:static z-20 transition-all duration-300 ease-in-out border-r',
        isCollapsed ? 'w-14' : 'w-64',
        isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      )}
      style={{ borderRightColor: 'hsl(var(--sidebar-right-border))' }}
    >
      {/* Sidebar header */}
      <div className="border-b border-sidebar-border h-[72px] flex items-center hover:bg-sidebar-accent overflow-hidden">
        <div className={clsx(
          'flex items-center w-full transition-all duration-300 ease-in-out justify-between',
          isCollapsed ? 'px-2' : 'px-4'
        )}>
          <NavLink
            to="/"
            className={clsx(
              'flex items-center text-xl font-bold whitespace-nowrap text-sidebar-foreground hover:text-sidebar-accent-foreground',
              isCollapsed ? 'gap-0' : 'gap-2'
            )}
            onClick={(e) => { if (isCollapsed) e.preventDefault() }}
          >
            <span
              onMouseEnter={triggerOrbCycle}
              className={clsx(
                'h-10 w-10 shrink-0 inline-flex items-center justify-center overflow-hidden transition-transform duration-300 ease-in-out origin-center',
                isCollapsed ? 'scale-[0.6]' : 'scale-100'
              )}
              aria-hidden="true"
            >
              <ResilienceIntelligenceOrb
                size={40}
                periodMs={3000}
                paused={!orbPlaying}
                showButton={false}
              />
            </span>
            <span className={clsx(
              'leading-none transition-all duration-300 ease-in-out overflow-hidden',
              isCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[160px]'
            )}>
              Operational<br />Resilience
            </span>
          </NavLink>
          <button
            type="button"
            aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!isCollapsed}
            onClick={toggleCollapse}
            className={clsx(
              'text-sidebar-foreground p-1 shrink-0 transition-all duration-300 ease-in-out rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
              isCollapsed ? '-ml-4' : 'ml-0'
            )}
          >
            <ChevronRight
              size={20}
              className={clsx(
                'transition-transform duration-300 ease-in-out',
                isCollapsed ? 'rotate-0' : 'rotate-180'
              )}
            />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 min-h-0">
        <nav className="pt-4 pb-4">
          {navigationSections.map((section, idx) => {
            const filteredItems = section.items.filter(isFeatureEnabled)
            if (filteredItems.length === 0) return null

            return (
              <div key={section.title}>
                {!isCollapsed && !section.hideTitle && (
                  <div className={clsx('px-4 mb-2', idx === 0 ? '' : 'mt-6')}>
                    <p className="text-xs font-semibold uppercase tracking-wider sidebar-header">
                      {section.title}
                    </p>
                  </div>
                )}
                {isCollapsed && idx > 0 && <div className="mt-6" />}
                <ul>
                  {filteredItems.map((item) => renderNavItem(item))}
                </ul>
              </div>
            )
          })}
        </nav>
      </ScrollArea>
    </aside>
  )
}
